import {
    EventClient,
    EventClientProvider,
    FormattedEvent,
} from '@eventbrite/event-renderer';
import { sdkRequest } from '@eventbrite/http';
import React, { useEffect, useState } from 'react';
import { CollectionResponse } from '../../types';
import { ShareProps } from '../ShareEvent/ShareEventRoot';
import { dynaMount } from '../utils/mountComponent';
import {
    excludeDuplicatedEvents,
    filterEvents,
    getOrganizerRelatedEventsFromDestination,
    getRelatedEvents,
    NUMBER_OF_MORE_EVENTS_TO_SHOW,
} from './api';
import MoreEventsFrom from './MoreEventsFrom';
import OtherEventsYouMayLike from './OtherEventsYouMayLike';
import {
    EventsFromCollectionResponse,
    transformCollectionEvent,
} from './transform';

const eventClient = new EventClient();

export interface RelatedEventsProps {
    eventId: string;
    isAuthenticated: boolean;
    shareComponentProps: ShareProps;
    events?: FormattedEvent[];
    organizerId: string;
    creatorCollections?: CollectionResponse[];
}

export type RelatedEventsChildrenProps = Omit<
    RelatedEventsProps,
    'organizerId' | 'eventId'
> & {
    isSingleCreatorCollection?: boolean;
    collectionUrl?: string;
};

export const RELATED_EVENTS_ID = 'listings-root__related-events';
export const MORE_EVENTS_FROM_ID =
    'listings-root__more-events-from-this-organizer';
export const RELATED_COLLECTIONS_ID =
    'listings-root__collections-related-to-this-event';

export const OtherEventsYouMayLikeMount = (
    props: RelatedEventsChildrenProps,
): JSX.Element | React.ReactPortal | null =>
    dynaMount(
        {
            Component: OtherEventsYouMayLike,
            containerId: RELATED_EVENTS_ID,
        },
        { ...props },
    );

export const MoreEventsFromMount = (
    props: RelatedEventsChildrenProps,
): JSX.Element | React.ReactPortal | null =>
    dynaMount(
        {
            Component: MoreEventsFrom,
            containerId: MORE_EVENTS_FROM_ID,
        },
        { ...props },
    );

export const RelatedEventsWrapper = (
    props: RelatedEventsProps,
): JSX.Element | null => {
    const { organizerId, eventId, creatorCollections } = props;

    const isSingleCreatorCollection = creatorCollections?.length === 1;
    const collectionUrl =
        (creatorCollections && creatorCollections[0]?.relative_url) ||
        undefined;
    const collectionId = creatorCollections && creatorCollections[0]?.id;

    const relatedEvents = useRelatedEvents(
        organizerId,
        eventId,
        creatorCollections?.length,
        collectionId,
    );

    return (
        <EventClientProvider client={eventClient}>
            <div data-testid="related-events-wrapper">
                {relatedEvents.eventsSameOrganizer && (
                    <MoreEventsFromMount
                        {...props}
                        events={relatedEvents.eventsSameOrganizer || []}
                    />
                )}
                {relatedEvents.eventsSameCollection && (
                    <MoreEventsFromMount
                        {...props}
                        events={relatedEvents.eventsSameCollection || []}
                        isSingleCreatorCollection={isSingleCreatorCollection}
                        collectionUrl={collectionUrl}
                    />
                )}
                {relatedEvents.eventsOtherOrganizers && (
                    <OtherEventsYouMayLikeMount
                        {...props}
                        events={relatedEvents.eventsOtherOrganizers}
                    />
                )}
            </div>
        </EventClientProvider>
    );
};

const useRelatedEvents = (
    organizerId: string,
    eventId: string,
    collectionsLength?: number,
    collectionId?: string,
) => {
    const [eventsSameOrganizer, setEventsSameOrganizer] =
        useState<FormattedEvent[]>();
    const [eventsSameCollection, setEventsSameCollection] =
        useState<FormattedEvent[]>();
    const [eventsOtherOrganizers, setEventsOtherOrganizers] =
        useState<FormattedEvent[]>();

    useEffect(() => {
        const fetchEventsSameOrganizer = () => {
            getOrganizerRelatedEventsFromDestination(organizerId, eventId)
                .then((response) => {
                    if (response) {
                        const { events } = response;
                        setEventsSameOrganizer(events);
                        return events;
                    }
                })
                .then((eventsSameOrganizer) => {
                    fetchEventsOtherOrganizers(eventsSameOrganizer);
                });
        };

        const fetchEventsSameCollection = () => {
            sdkRequest(
                `/collections/${collectionId}/events/public?time_filter=current_future`,
            )
                .then((response: EventsFromCollectionResponse) => {
                    const filteredEvents = filterEvents(
                        NUMBER_OF_MORE_EVENTS_TO_SHOW,
                        response.events,
                        eventId,
                    );
                    const transformedEvents = filteredEvents.map((event) => {
                        return transformCollectionEvent(event);
                    });
                    setEventsSameCollection(transformedEvents);
                    return transformedEvents;
                })
                .then((eventsSameCollection: FormattedEvent[]) => {
                    fetchEventsOtherOrganizers(eventsSameCollection);
                });
        };

        const fetchEventsOtherOrganizers = (eventsFrom?: FormattedEvent[]) => {
            const _getRelatedEventsOtherOrganizersApi = async (
                eventId: string,
                eventsFromIds: string[] = [],
            ) => {
                const relatedEvents = await getRelatedEvents(eventId);
                const filteredEvents =
                    eventsFromIds.length > 0
                        ? excludeDuplicatedEvents(
                              relatedEvents.events,
                              eventsFromIds,
                          )
                        : relatedEvents.events;

                return filteredEvents;
            };

            const shouldShowRelatedEventsLive =
                document.getElementById(RELATED_EVENTS_ID);

            const eventsFromIds: string[] | undefined = eventsFrom?.map(
                (event) => event.id,
            );

            if (shouldShowRelatedEventsLive) {
                _getRelatedEventsOtherOrganizersApi(
                    eventId,
                    eventsFromIds,
                ).then((filteredEvents) => {
                    setEventsOtherOrganizers(filteredEvents);
                });
            }
        };

        if (collectionsLength === 0) {
            fetchEventsSameOrganizer();
        } else if (collectionsLength === 1) {
            fetchEventsSameCollection();
        } else if (collectionsLength && collectionsLength >= 1) {
            fetchEventsOtherOrganizers();
        }
    }, [collectionId, collectionsLength, eventId, organizerId]);

    return { eventsSameOrganizer, eventsSameCollection, eventsOtherOrganizers };
};

export default RelatedEventsWrapper;
